/**
 * Focused Subgraph - Extract and display small portions as traditional diagrams
 * Issue #462: Keep React Flow diagram but only for extracted subgraphs (<100 nodes)
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  Handle,
  Position,
  MarkerType,
  useNodesState,
  useEdgesState,
  type Node,
  type Edge,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import type { CallGraphData, CallGraphNode, CallGraphEdge } from '@veodiagram/shared';
import type { DiagramMetadata } from '@veodiagram/shared';
import { applyLayout, type LayoutDirection, type LayoutAlgorithm } from '../diagram/renderers/shared/layoutEngine';
import { SubgraphLayoutControls } from '../diagram/renderers/shared/SubgraphLayoutControls';
import { FloatingEdge } from '../diagram/renderers/shared/FloatingEdge';

interface FocusedSubgraphProps {
  data: CallGraphData;
  rootFunctionId: string;
  direction?: 'callers' | 'callees' | 'both';
  maxDepth?: number;
  onSelectFunction?: (functionId: string) => void;
  onBack?: () => void;
  onClose?: () => void;
  // Navigation history — list of function IDs visited before the current root
  historyFunctionIds?: string[];
  onNavigateToHistoryIndex?: (i: number) => void;
}

// veodiagram-issues#45.B — database cylinder icon. Sibling copy of
// the one in CallGraphNodes.tsx because FocusedSubgraph uses its own
// simplified node component. Different `data-testid` namespace
// (`-subgraph` suffix) so `getByTestId` doesn't collide if a future
// integration test mounts both surfaces in one render. R2#5 fix.
function DatabaseIcon() {
  return (
    <svg
      width="14" height="14" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth={2}
      role="img" aria-label="Database call" data-testid="bcg-db-icon-subgraph"
      style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: 4 }}
    >
      <title>Database call</title>
      <ellipse cx="12" cy="5" rx="8" ry="2.5" />
      <path strokeLinecap="round" d="M4 5v6c0 1.4 3.6 2.5 8 2.5s8-1.1 8-2.5V5" />
      <path strokeLinecap="round" d="M4 11v6c0 1.4 3.6 2.5 8 2.5s8-1.1 8-2.5v-6" />
    </svg>
  );
}

// R2#2 fix — keep BCG-kind colors aligned with the canonical
// `BCG_KIND_COLORS` table in CallGraphNodes.tsx. Using hex literals
// here (rather than Tailwind classes) keeps the two views visually
// identical; Tailwind's `border-emerald-600` is #059669 which doesn't
// match the canonical #0f766e and produced visible drift in R1.
const BCG_INLINE_COLORS = {
  DbCall:              { bg: '#d1fae5', border: '#0f766e', text: '#134e4a' },
  ExternalCallHttp:    { bg: '#f5f5f5', border: '#616161', text: '#424242' },
  ExternalCallSdk:     { bg: '#fafafa', border: '#9e9e9e', text: '#616161' },
  ExternalCallUnknown: { bg: '#ffebee', border: '#c62828', text: '#b71c1c' },
} as const;

// R2#6 fix — narrow at the boundary instead of trusting an `as` cast.
// The strict equality checks downstream are safe either way, but the
// explicit narrowing keeps the type-system honest and matches the
// `BackendGraphPage` whitelist guard pattern (R1#8).
function narrowBcgKind(v: unknown): 'DbCall' | 'ExternalCall' | undefined {
  return v === 'DbCall' || v === 'ExternalCall' ? v : undefined;
}
function narrowCategory(v: unknown): 'sdk' | 'http' | undefined {
  return v === 'sdk' || v === 'http' ? v : undefined;
}

/**
 * Custom node component for the BCG subgraph view.
 *
 * @internal — exported only so unit tests can render it directly
 * without the async layout machinery in FocusedSubgraph (which never
 * settles inside JSDOM). Do not import from outside this module or
 * its sibling test file.
 */
export function FunctionNode({ data }: { data: Record<string, unknown> }) {
  const type = data.type as string;
  const isRoot = data.isRoot as boolean;
  const dimmed = data.dimmed as boolean;
  const highlighted = data.highlighted as boolean;
  // veodiagram-issues#45.B — BCG-only fields piped through by the
  // subgraph builder. Renderer branches on these when present;
  // generic call graphs (no bcgKind) keep the original styling.
  const bcgKind = narrowBcgKind(data.bcgKind);
  const category = narrowCategory(data.category);
  const host = typeof data.host === 'string' ? data.host : undefined;
  const urlPattern = typeof data.urlPattern === 'string' ? data.urlPattern : undefined;
  const service = typeof data.service === 'string' ? data.service : undefined;

  // Returns either Tailwind classes (legacy palettes) or `null` when
  // BCG-kind override applies (caller applies inline styles instead).
  const getTypeColorClass = (): string | null => {
    if (isRoot) return 'bg-blue-500 text-white border-blue-700';
    if (bcgKind) return null; // inline styles below
    switch (type) {
      case 'entry': return 'bg-green-100 text-green-800 border-green-400';
      case 'function': return 'bg-blue-100 text-blue-800 border-blue-400';
      case 'method': return 'bg-purple-100 text-purple-800 border-purple-400';
      case 'arrow': return 'bg-pink-100 text-pink-800 border-pink-400';
      case 'constructor': return 'bg-amber-100 text-amber-800 border-amber-400';
      case 'external': return 'bg-red-100 text-red-800 border-red-400';
      default: return 'bg-gray-100 text-gray-800 border-gray-400';
    }
  };

  const bcgInline = (() => {
    if (!bcgKind) return null;
    if (bcgKind === 'DbCall') return BCG_INLINE_COLORS.DbCall;
    if (category === 'http') return BCG_INLINE_COLORS.ExternalCallHttp;
    if (category === 'sdk') return BCG_INLINE_COLORS.ExternalCallSdk;
    return BCG_INLINE_COLORS.ExternalCallUnknown;
  })();

  // All 4 sides have handles so FloatingEdge can connect from whichever side is closest
  const handleStyle = { opacity: 0, width: 6, height: 6 };

  const colorClass = getTypeColorClass() ?? '';
  return (
    <div
      className={`px-4 py-2 rounded-lg border-2 shadow-md min-w-[120px] ${colorClass} ${highlighted ? 'ring-2 ring-yellow-400 ring-offset-1' : ''}`}
      style={{
        opacity: dimmed ? 0.3 : 1,
        transition: 'opacity 0.2s',
        ...(bcgInline && !isRoot
          ? {
              backgroundColor: bcgInline.bg,
              borderColor: bcgInline.border,
              color: bcgInline.text,
            }
          : {}),
      }}
    >
      <Handle type="target" position={Position.Top} style={handleStyle} />
      <Handle type="target" position={Position.Bottom} style={handleStyle} />
      <Handle type="target" position={Position.Left} style={handleStyle} />
      <Handle type="target" position={Position.Right} style={handleStyle} />
      <div className="font-semibold text-sm">
        {bcgKind === 'DbCall' && <DatabaseIcon />}
        {data.label as string}
      </div>
      {/* #45.B — for ExternalCall+http show host above module. For
          everything else (and SDK/unknown without a host) show the
          module as before. */}
      {bcgKind === 'ExternalCall' && category === 'http' && host ? (
        <div className="text-xs opacity-75" title={urlPattern || host}>{host}</div>
      ) : (
        <div className="text-xs opacity-75">{data.module as string}</div>
      )}
      {/* #45.B — BCG-kind pills, mirror of CallGraphNodes.tsx. */}
      {bcgKind === 'ExternalCall' && (
        <div className="mt-1">
          {category === 'http' && (
            <span className="text-[9px] px-1 py-0.5 rounded bg-gray-200 text-gray-700 border border-gray-300"
                  title={urlPattern ? `Outbound HTTP → ${urlPattern}` : 'Outbound HTTP call'}>
              ↗ HTTP
            </span>
          )}
          {category === 'sdk' && (
            <span className="text-[9px] px-1 py-0.5 rounded bg-gray-100 text-gray-600"
                  title={service ? `SDK method on ${service}` : 'External SDK method'}>
              SDK
            </span>
          )}
          {category !== 'http' && category !== 'sdk' && (
            <span className="text-[9px] px-1 py-0.5 rounded bg-red-100 text-red-700 border border-red-300"
                  title="External call (category not detected by analyzer)">
              External (?)
            </span>
          )}
        </div>
      )}
      <Handle type="source" position={Position.Top} style={handleStyle} />
      <Handle type="source" position={Position.Bottom} style={handleStyle} />
      <Handle type="source" position={Position.Left} style={handleStyle} />
      <Handle type="source" position={Position.Right} style={handleStyle} />
    </div>
  );
}

const nodeTypes = {
  function: FunctionNode,
};

const edgeTypes = {
  floating: FloatingEdge,
};

export function FocusedSubgraph({
  data,
  rootFunctionId,
  direction = 'both',
  maxDepth = 2,
  onSelectFunction,
  onBack,
  onClose,
  historyFunctionIds,
  onNavigateToHistoryIndex,
}: FocusedSubgraphProps) {
  const [depth, setDepth] = useState(maxDepth);
  const [selectedDirection, setSelectedDirection] = useState(direction);
  const [searchQuery, setSearchQuery] = useState('');
  const [layoutDirection, setLayoutDirection] = useState<LayoutDirection>('TB');
  const [layoutAlgorithm, setLayoutAlgorithm] = useState<LayoutAlgorithm>('dagre');
  const [hideExternal, setHideExternal] = useState(false);
  const [showMiniMap, setShowMiniMap] = useState(true);
  const [layoutPositions, setLayoutPositions] = useState<Map<string, { x: number; y: number }> | null>(null);
  const [isLayouting, setIsLayouting] = useState(false);

  // Build lookup maps
  const nodeMap = useMemo(() => {
    const map = new Map<string, CallGraphNode>();
    for (const node of data.nodes) {
      map.set(node.id, node);
    }
    return map;
  }, [data.nodes]);

  const edgesBySource = useMemo(() => {
    const map = new Map<string, CallGraphEdge[]>();
    for (const edge of data.edges) {
      const existing = map.get(edge.source) || [];
      existing.push(edge);
      map.set(edge.source, existing);
    }
    return map;
  }, [data.edges]);

  const edgesByTarget = useMemo(() => {
    const map = new Map<string, CallGraphEdge[]>();
    for (const edge of data.edges) {
      const existing = map.get(edge.target) || [];
      existing.push(edge);
      map.set(edge.target, existing);
    }
    return map;
  }, [data.edges]);

  // Extract subgraph
  const { subgraphNodes, subgraphEdges, nodeDirection } = useMemo(() => {
    const includedNodes = new Set<string>();
    const includedEdges = new Set<string>();
    // Track whether each node was reached as a caller or callee of root
    const nodeDirection = new Map<string, 'caller' | 'callee' | 'root'>();

    // BFS to find nodes within depth
    const queue: Array<{ id: string; currentDepth: number; direction: 'caller' | 'callee' }> = [];

    // Start from root
    includedNodes.add(rootFunctionId);
    nodeDirection.set(rootFunctionId, 'root');

    // Add initial neighbors based on direction
    if (selectedDirection === 'callers' || selectedDirection === 'both') {
      const callerEdges = edgesByTarget.get(rootFunctionId) || [];
      for (const edge of callerEdges) {
        queue.push({ id: edge.source, currentDepth: 1, direction: 'caller' });
      }
    }

    if (selectedDirection === 'callees' || selectedDirection === 'both') {
      const calleeEdges = edgesBySource.get(rootFunctionId) || [];
      for (const edge of calleeEdges) {
        queue.push({ id: edge.target, currentDepth: 1, direction: 'callee' });
      }
    }

    // BFS
    while (queue.length > 0) {
      const { id, currentDepth, direction: dir } = queue.shift()!;

      if (includedNodes.has(id)) continue;
      if (currentDepth > depth) continue;
      if (hideExternal && nodeMap.get(id)?.type === 'external') continue;

      includedNodes.add(id);
      nodeDirection.set(id, dir);

      if (currentDepth < depth) {
        // Continue in the same direction
        if (dir === 'caller') {
          const callerEdges = edgesByTarget.get(id) || [];
          for (const edge of callerEdges) {
            if (!includedNodes.has(edge.source)) {
              queue.push({ id: edge.source, currentDepth: currentDepth + 1, direction: 'caller' });
            }
          }
        } else {
          const calleeEdges = edgesBySource.get(id) || [];
          for (const edge of calleeEdges) {
            if (!includedNodes.has(edge.target)) {
              queue.push({ id: edge.target, currentDepth: currentDepth + 1, direction: 'callee' });
            }
          }
        }
      }
    }

    // Collect edges between included nodes
    for (const edge of data.edges) {
      if (includedNodes.has(edge.source) && includedNodes.has(edge.target)) {
        includedEdges.add(edge.id);
      }
    }

    // Build node and edge arrays
    const nodes = data.nodes.filter(n => includedNodes.has(n.id));
    const edges = data.edges.filter(e => includedEdges.has(e.id));

    return { subgraphNodes: nodes, subgraphEdges: edges, nodeDirection };
  }, [data.nodes, data.edges, rootFunctionId, depth, selectedDirection, edgesBySource, edgesByTarget, hideExternal, nodeMap]);

  // Async layout: run applyLayout whenever subgraph or layout settings change
  useEffect(() => {
    if (subgraphNodes.length === 0) {
      setLayoutPositions(new Map());
      return;
    }
    let cancelled = false;
    setIsLayouting(true);

    const layoutNodes = subgraphNodes.map(n => ({
      id: n.id,
      type: 'service' as const,
      label: n.name,
      position: { x: 0, y: 0 },
      // Use actual rendered sizes so dagre spaces nodes correctly (issue #611)
      width: 200,
      height: 72,
    }));
    const layoutEdges = subgraphEdges.map(e => ({
      id: e.id,
      source: e.source,
      target: e.target,
      type: 'default' as const,
      animated: false,
    }));

    applyLayout(
      layoutNodes as Parameters<typeof applyLayout>[0],
      layoutEdges as Parameters<typeof applyLayout>[1],
      { title: 'subgraph', layoutDirection, layoutAlgorithm } as DiagramMetadata,
    ).then(result => {
      if (cancelled) return;
      setLayoutPositions(new Map(result.map(n => [n.id, n.position])));
      setIsLayouting(false);
    }).catch(() => {
      if (!cancelled) setIsLayouting(false);
    });

    return () => { cancelled = true; };
  }, [subgraphNodes, subgraphEdges, layoutDirection, layoutAlgorithm]);

  // Build React Flow nodes from layout positions
  const flowNodes: Node[] = useMemo(() => {
    if (!layoutPositions) return [];
    const query = searchQuery.trim().toLowerCase();
    const hasSearch = query.length > 0;

    return subgraphNodes.map(node => {
      const pos = layoutPositions.get(node.id) ?? { x: 0, y: 0 };
      const matches = hasSearch && (
        node.name.toLowerCase().includes(query) ||
        (node.module || '').toLowerCase().includes(query)
      );
      // #45.B — propagate BCG-only fields from CallGraphNode.data
      // through to the local FunctionNode so it can branch on bcgKind.
      const ext = (node.data ?? {}) as Record<string, unknown>;
      return {
        id: node.id,
        type: 'function',
        position: pos,
        data: {
          label: node.name,
          module: node.module,
          type: node.type,
          isRoot: node.id === rootFunctionId,
          qualifiedName: node.qualifiedName,
          layoutDirection,
          dimmed: hasSearch && !matches,
          highlighted: hasSearch && matches,
          bcgKind: ext.bcgKind,
          service: ext.service,
          host: ext.host,
          urlPattern: ext.urlPattern,
          category: ext.category,
        },
      };
    });
  }, [layoutPositions, subgraphNodes, rootFunctionId, layoutDirection, searchQuery]);

  // Build React Flow edges with direction-based colour coding
  const flowEdges: Edge[] = useMemo(() => subgraphEdges.map(edge => {
    // An edge is a "caller" edge if its source is on the caller side (i.e. source calls root/callee-side)
    // An edge is a "callee" edge if its source is root or on the callee side
    const sourceDir = nodeDirection.get(edge.source);
    const isCallerEdge = sourceDir === 'caller';
    const isCalleeEdge = sourceDir === 'root' || sourceDir === 'callee';

    const colour = edge.isAsync
      ? '#f97316'           // orange — async
      : isCallerEdge
        ? '#3b82f6'         // blue — caller → root direction
        : isCalleeEdge
          ? '#22c55e'       // green — root → callee direction
          : '#64748b';      // grey — fallback

    return {
      id: edge.id,
      source: edge.source,
      target: edge.target,
      type: 'floating',
      animated: edge.isAsync,
      style: {
        stroke: colour,
        strokeWidth: Math.min(edge.callCount, 5),
      },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: colour,
        width: 16,
        height: 16,
      },
      label: edge.callCount > 1 ? `${edge.callCount}x` : undefined,
      labelStyle: { fontSize: 10 },
    };
  }), [subgraphEdges, nodeDirection]);

  const [nodes, setNodes, onNodesChange] = useNodesState(flowNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(flowEdges);

  // Update nodes/edges when subgraph changes (use useEffect, not useMemo)
  useEffect(() => {
    setNodes(flowNodes);
    setEdges(flowEdges);
  }, [flowNodes, flowEdges, setNodes, setEdges]);

  // Handle node click
  const onNodeClick = useCallback((_: React.MouseEvent, node: Node) => {
    onSelectFunction?.(node.id);
  }, [onSelectFunction]);

  const rootNode = nodeMap.get(rootFunctionId);

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-900">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 dark:text-white truncate">
            Subgraph: {rootNode?.name || rootFunctionId}
          </h3>
          {/* Breadcrumb navigation history */}
          {historyFunctionIds && historyFunctionIds.length > 0 && (
            <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 mt-0.5 overflow-x-auto">
              {historyFunctionIds.map((id, i) => (
                <span key={`${id}-${i}`} className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => onNavigateToHistoryIndex?.(i)}
                    className="text-blue-600 dark:text-blue-400 hover:underline truncate max-w-[120px]"
                    title={nodeMap.get(id)?.qualifiedName || id}
                  >
                    {nodeMap.get(id)?.name ?? id}
                  </button>
                  <span className="text-gray-400">›</span>
                </span>
              ))}
              <span className="font-medium text-gray-700 dark:text-gray-300 truncate max-w-[120px]">
                {rootNode?.name || rootFunctionId}
              </span>
            </div>
          )}
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {subgraphNodes.length} nodes, {subgraphEdges.length} edges
          </p>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600 dark:text-gray-400">Direction:</label>
          <select
            value={selectedDirection}
            onChange={(e) => setSelectedDirection(e.target.value as 'callers' | 'callees' | 'both')}
            className="px-3 py-1 border border-gray-300 rounded dark:bg-gray-800 dark:border-gray-600 dark:text-white"
          >
            <option value="both">Both</option>
            <option value="callers">Callers Only</option>
            <option value="callees">Callees Only</option>
          </select>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="text"
            placeholder="Search nodes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="px-3 py-1 border border-gray-300 rounded dark:bg-gray-800 dark:border-gray-600 dark:text-white text-sm w-40"
          />
        </div>

        <SubgraphLayoutControls
          layoutDirection={layoutDirection}
          layoutAlgorithm={layoutAlgorithm}
          onDirectionChange={setLayoutDirection}
          onAlgorithmChange={setLayoutAlgorithm}
          isLayouting={isLayouting}
        />

        <label className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400 cursor-pointer">
          <input
            type="checkbox"
            checked={hideExternal}
            onChange={(e) => setHideExternal(e.target.checked)}
            className="rounded"
          />
          Hide external
        </label>

        <label className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400 cursor-pointer">
          <input
            type="checkbox"
            checked={showMiniMap}
            onChange={(e) => setShowMiniMap(e.target.checked)}
            className="rounded"
          />
          Minimap
        </label>

        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600 dark:text-gray-400">Depth:</label>
          <input
            type="range"
            min="1"
            max="5"
            value={depth}
            onChange={(e) => setDepth(parseInt(e.target.value))}
            className="w-20"
          />
          <span className="text-sm text-gray-600 dark:text-gray-400 w-4">{depth}</span>
        </div>

        {onBack && (
          <button
            onClick={onBack}
            className="px-3 py-1 bg-gray-200 dark:bg-gray-700 rounded hover:bg-gray-300 dark:hover:bg-gray-600 dark:text-white text-sm"
            title="Go back to previous subgraph"
          >
            ← Back
          </button>
        )}

        {onClose && (
          <button
            onClick={onClose}
            className="px-3 py-1 bg-gray-200 dark:bg-gray-700 rounded hover:bg-gray-300 dark:hover:bg-gray-600 dark:text-white text-sm"
            title="Close subgraph and return to explorer"
          >
            ✕ Close
          </button>
        )}
      </div>

      {/* Warning if too many nodes */}
      {subgraphNodes.length > 100 && (
        <div className="px-4 py-2 bg-amber-50 dark:bg-amber-900/30 border-b border-amber-200 dark:border-amber-700 text-amber-800 dark:text-amber-200 text-sm">
          Large subgraph ({subgraphNodes.length} nodes). Consider reducing depth for better performance.
        </div>
      )}

      {/* React Flow Diagram */}
      <div className="flex-1" style={{ minHeight: '400px' }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={onNodeClick}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          fitView
          minZoom={0.1}
          maxZoom={2}
        >
          <Background />
          <Controls />
          {showMiniMap && (
            <MiniMap
              nodeColor={(node) => node.data?.isRoot ? '#3b82f6' : '#94a3b8'}
              maskColor="rgba(0, 0, 0, 0.1)"
              style={{ width: 160, height: 100 }}
              {...{ pannable: true, zoomable: true }}
            />
          )}
        </ReactFlow>
      </div>

      {/* Legend */}
      <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 flex items-center gap-4 text-sm flex-wrap">
        <span className="text-gray-600 dark:text-gray-400">Nodes:</span>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 bg-blue-500 rounded" />
          <span>Root</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 bg-green-100 border border-green-400 rounded" />
          <span>Entry</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 bg-blue-100 border border-blue-400 rounded" />
          <span>Function</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 bg-red-100 border border-red-400 rounded" />
          <span>External</span>
        </div>
        <span className="text-gray-400 dark:text-gray-500">|</span>
        <span className="text-gray-600 dark:text-gray-400">Edges:</span>
        <div className="flex items-center gap-1">
          <div className="w-8 h-0.5 bg-blue-500" />
          <span>Caller → Root</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-8 h-0.5 bg-green-500" />
          <span>Root → Callee</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-8 h-0.5 bg-orange-500" />
          <span>Async</span>
        </div>
      </div>
    </div>
  );
}

export default FocusedSubgraph;
